import { DEFAULT_COLUMN_HEADER_HEIGHT, GRID_ROW_NUMBER_COL_DEF } from '@repo/go-table';
import { equalsDeep } from '@repo/utils';
import { action, computed, makeObservable, observable } from 'mobx';

import {
  DEFAULT_COLUMN_HEADER_ORIENTATION,
  DEFAULT_NUMBER_DECIMAL_PLACES,
  MAX_NUMBER_DECIMAL_PLACES,
  MIN_NUMBER_DECIMAL_PLACES,
} from '../../consts';
import type { TrajectoryTableSettingsModel } from '../../types/host-services';

export const SERVICE_COLUMNS = [GRID_ROW_NUMBER_COL_DEF.field];
export const MIN_VISIBLE_COLUMNS = 3;
export const DEFAULT_USE_SHORT_COLUMN_HEADERS = true;

export class DisplayManager {
  @observable private _settings: TrajectoryTableSettingsModel;

  constructor(
    initialSettings: TrajectoryTableSettingsModel,
    private readonly onSettingsChange: (settings: TrajectoryTableSettingsModel) => void,
  ) {
    this._settings = initialSettings;

    makeObservable(this);
  }

  @action.bound
  private updateSettings(newSettings: TrajectoryTableSettingsModel) {
    if (equalsDeep(newSettings, this._settings)) {
      return;
    }

    this._settings = newSettings;
    this.onSettingsChange(newSettings);
  }

  @computed
  get settings(): TrajectoryTableSettingsModel {
    return this._settings;
  }

  @computed
  get displaySettingPinnedColumns(): string[] {
    const excludeColumns = [GRID_ROW_NUMBER_COL_DEF.field];

    const leftPinnedColumns = this._settings.pinnedColumns?.left || [];
    const rightPinnedColumns = this._settings.pinnedColumns?.right || [];

    const includedLeftColumns = leftPinnedColumns.filter((column: string) => !excludeColumns.includes(column));

    return [...includedLeftColumns, ...rightPinnedColumns];
  }

  @computed
  get displaySettingColumns(): string[] {
    const displayedColumns = this._settings?.columnsOrderModel?.filter(
      (column: string) => !this.displaySettingPinnedColumns.includes(column),
    );

    return displayedColumns ?? [];
  }

  @computed
  get columnsVisibilityModel() {
    return this._settings.columnsVisibility ?? {};
  }

  @computed
  get sortingModel() {
    return this._settings.sortingModel;
  }

  @computed
  get columnsOrderModel() {
    return this._settings.columnsOrderModel;
  }

  @computed
  get pinnedColumns() {
    return this._settings.pinnedColumns;
  }

  @computed
  get columnsWidth() {
    return this._settings.columnsWidth;
  }

  @computed
  get useShortColumnHeaders(): boolean {
    return this._settings.useShortColumnHeaders ?? DEFAULT_USE_SHORT_COLUMN_HEADERS;
  }

  @computed
  get numberDecimalPlaces(): number {
    return this._settings.numberDecimalPlaces ?? DEFAULT_NUMBER_DECIMAL_PLACES;
  }

  @computed
  get columnHeaderOrientation(): NonNullable<TrajectoryTableSettingsModel['columnHeaderOrientation']> {
    return this._settings.columnHeaderOrientation ?? DEFAULT_COLUMN_HEADER_ORIENTATION;
  }

  @computed
  get columnHeaderHeight(): number | 'auto' {
    if (this.columnHeaderOrientation === 'horizontal') {
      return DEFAULT_COLUMN_HEADER_HEIGHT;
    }

    return this._settings.columnHeaderHeight ?? 'auto';
  }

  @computed
  get isColumnHeaderHeightResizable(): boolean {
    return this.columnHeaderOrientation === 'vertical';
  }

  @action.bound
  onColumnsOrderModelChange(columns: string[]): void {
    this.updateSettings({ ...this._settings, columnsOrderModel: columns });
  }

  @action.bound
  onSortChange(model: TrajectoryTableSettingsModel['sortingModel']): void {
    this.updateSettings({ ...this._settings, sortingModel: model });
  }

  @action.bound
  onPinChange(pinnedColumns: TrajectoryTableSettingsModel['pinnedColumns']): void {
    this.updateSettings({ ...this._settings, pinnedColumns });
  }

  @action.bound
  onWidthChange(incomeColumnsWidth: Record<string, number>): void {
    const newWidthSettings: Record<string, number> = { ...this._settings.columnsWidth, ...incomeColumnsWidth };
    this.updateSettings({ ...this._settings, columnsWidth: newWidthSettings });
  }

  @action.bound
  onColumnVisibilityChange(model: Record<string, boolean>): void {
    this.updateSettings({ ...this._settings, columnsVisibility: model });
  }

  @action.bound
  onUseShortColumnHeadersChange(useShortColumnHeaders: boolean): void {
    this.updateSettings({ ...this._settings, useShortColumnHeaders });
  }

  @action.bound
  onNumberDecimalPlacesChange(numberDecimalPlaces: number): void {
    const next = Math.min(
      MAX_NUMBER_DECIMAL_PLACES,
      Math.max(MIN_NUMBER_DECIMAL_PLACES, Math.round(numberDecimalPlaces)),
    );
    this.updateSettings({ ...this._settings, numberDecimalPlaces: next });
  }

  @action.bound
  onColumnHeaderOrientationChange(
    columnHeaderOrientation: NonNullable<TrajectoryTableSettingsModel['columnHeaderOrientation']>,
  ): void {
    if (columnHeaderOrientation === 'vertical') {
      this.updateSettings({
        ...this._settings,
        columnHeaderOrientation,
        columnHeaderHeight: undefined,
      });
      return;
    }

    this.updateSettings({ ...this._settings, columnHeaderOrientation });
  }

  @action.bound
  onColumnHeaderHeightChange(columnHeaderHeight: number): void {
    this.updateSettings({ ...this._settings, columnHeaderHeight });
  }
}
